'use client';

import { CodeInput } from '@lulalife/design-system';
import get from 'lodash/get';
import { Controller, useFormContext } from 'react-hook-form';

export interface CodeInputFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  'data-testid'?: string;
}

/**
 * The six-box DS `CodeInput`, bound to a react-hook-form field by `name` like the sibling
 * `InputField`. The form value is the joined digit string, exactly what the single text field
 * held before, so a submit handler reads `data.code` as it always did. A field error shows as
 * the boxes' red state with the message under them.
 */
export const CodeInputField = ({
  name,
  label,
  disabled,
  autoFocus,
  'data-testid': dataTestId,
}: CodeInputFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const fieldError = get(errors, name);
  const fieldErrorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CodeInput
          id={name}
          label={label}
          value={field.value ?? ''}
          disabled={disabled}
          autoFocus={autoFocus}
          error={Boolean(fieldError)}
          hint={fieldErrorMessage}
          data-testid={dataTestId ?? `${name}-input`}
          inputProps={{ onBlur: field.onBlur }}
          onChange={field.onChange}
        />
      )}
    />
  );
};

export default CodeInputField;
